import { Button } from './primitives';

const SECTIONS: { title: string; keys: [string, string][] }[] = [
  {
    title: 'Transport',
    keys: [
      ['Enter', 'Play from the cursor row'],
      ['Shift + Enter', 'Play from the top of the pattern'],
      ['Shift + Space', 'Play / pause'],
      ['Esc', 'Stop and leave edit mode'],
      ['Space', 'Toggle edit mode'],
    ],
  },
  {
    title: 'Playing notes',
    keys: [
      ['Z S X D C V G B H N J M', 'Lower octave, white and black keys'],
      ['Q 2 W 3 E R 5 T 6 Y 7 U', 'Upper octave'],
      ['[ / ]', 'Octave down / up'],
      ['F1 – F8', 'Jump straight to an octave'],
      ['`', 'Key off (releases the envelope)'],
    ],
  },
  {
    title: 'Moving around',
    keys: [
      ['Arrows', 'Move the cursor'],
      ['Tab / Shift + Tab', 'Next / previous channel'],
      ['Page Up / Down', 'Move 16 rows'],
      ['Home / End', 'First / last row'],
      ['Shift + Arrows', 'Extend the selection'],
      ['Click + drag', 'Select a block'],
    ],
  },
  {
    title: 'Editing',
    keys: [
      ['0 – 9, A – F', 'Hex entry in the number columns'],
      ['A – Z', 'Effect letter in the effect column'],
      ['Delete', 'Clear the cell or selection'],
      ['Insert / Backspace', 'Insert / delete a row in this channel'],
      ['Ctrl + C / X / V', 'Copy, cut, paste a block'],
      ['Ctrl + A', 'Select the whole pattern'],
      ['Ctrl + Z / Y', 'Undo / redo'],
      ['Ctrl + ↑ / ↓', 'Octave up / down'],
    ],
  },
  {
    title: 'Mixer',
    keys: [
      ['Click a scope', 'Mute that channel'],
      ['Shift + click a scope', 'Solo that channel'],
    ],
  },
];

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
    >
      <div
        className="max-h-full w-full max-w-4xl overflow-y-auto rounded-xl border border-ink-600 bg-ink-850 p-6 shadow-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-sans text-lg font-bold text-white">Keyboard</h2>
            <p className="mt-1 font-mono text-[11px] text-ink-400">
              The layout is FastTracker&nbsp;II&apos;s, so old muscle memory still works.
            </p>
          </div>
          <Button onClick={onClose}>Close</Button>
        </div>

        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-acid-500">
                {section.title}
              </h3>
              <dl className="space-y-1">
                {section.keys.map(([key, description]) => (
                  <div key={key} className="flex items-baseline justify-between gap-3">
                    <dt className="shrink-0 font-mono text-[11px] text-white">{key}</dt>
                    <dd className="min-w-0 flex-1 border-b border-dotted border-ink-700 text-right font-mono text-[10px] text-ink-400">
                      {description}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div className="mt-6 border-t border-ink-700 pt-4 font-mono text-[10px] leading-relaxed text-ink-500">
          <p>
            <span className="text-ink-300">Edit mode</span> is what separates jamming from writing.
            With it off, the keyboard just plays notes. With it on, every note you press is written
            into the pattern at the cursor — or at the playing row if the transport is running and
            Follow is on, which is how you record a part live.
          </p>
        </div>
      </div>
    </div>
  );
}
